import React from 'react';

/**
 * Editorial / creative components to enrich the AdAura experience.
 * Keep all motion CSS-driven (no JS animation loops) so perf stays clean.
 */

// ─────────────────────────────────────────────
// 1. Film grain overlay — fixed full-page texture
// ─────────────────────────────────────────────
export const GrainOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[1] opacity-[0.03] mix-blend-soft-light"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      backgroundSize: '160px 160px',
    }}
  />
);

// ─────────────────────────────────────────────
// 2. Gold dust — slow drifting particles (CSS-only)
// ─────────────────────────────────────────────
export const GoldDust = ({ density = 14 }) => {
  const particles = Array.from({ length: density });
  return (
    <div aria-hidden className="gold-dust-layer pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((_, i) => {
        const size = (Math.random() * 2 + 1).toFixed(2);
        const left = (Math.random() * 100).toFixed(2);
        const delay = (Math.random() * 16).toFixed(2);
        const duration = (Math.random() * 14 + 18).toFixed(2);
        const opacity = (Math.random() * 0.45 + 0.15).toFixed(2);
        const drift = (Math.random() * 60 - 30).toFixed(0);
        return (
          <span
            key={i}
            className="gold-dust"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              left: `${left}%`,
              opacity,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
              '--drift': `${drift}px`,
            }}
          />
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// 3. Kinetic marquee — endless scrolling band
// ─────────────────────────────────────────────
export const KineticMarquee = ({ words = [], speed = 50, dark = false }) => {
  const items = Array.isArray(words) ? words : [];
  if (!items.length) return null;
  return (
    <div className={`relative overflow-hidden ${dark ? 'bg-[#463724]' : ''} border-y border-[#C9A56C]/15 py-6`}>
      <div
        className="kinetic-track flex whitespace-nowrap"
        style={{ animationDuration: `${speed}s` }}
      >
        {[0, 1].map((loop) => (
          <div key={loop} className="flex shrink-0 items-center">
            {items.map((w, i) => (
              <React.Fragment key={`${loop}-${i}`}>
                <span className="font-[Libre_Baskerville] italic text-3xl md:text-5xl lg:text-6xl text-white/85 tracking-tight px-8 md:px-12">
                  {w}
                </span>
                <span aria-hidden className="text-[#C9A56C] text-2xl md:text-4xl px-2">✦</span>
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// 4. Section divider — animated gold sweep line
// ─────────────────────────────────────────────
export const GoldSweepDivider = ({ label }) => (
  <div className="relative py-16 md:py-20" aria-hidden>
    <div className="flex items-center gap-6 max-w-6xl mx-auto px-6 lg:px-10">
      <span className="flex-1 h-px bg-gradient-to-r from-transparent via-[#C9A56C]/50 to-[#C9A56C]/60 origin-left scale-x-0 gold-sweep-in" />
      {label && (
        <span className="text-[#C9A56C] tracking-[0.5em] uppercase text-[10px] md:text-[11px] whitespace-nowrap">
          {label}
        </span>
      )}
      <span className="flex-1 h-px bg-gradient-to-l from-transparent via-[#C9A56C]/50 to-[#C9A56C]/60 origin-right scale-x-0 gold-sweep-in" />
    </div>
  </div>
);

// ─────────────────────────────────────────────
// 5. Light leak — soft animated radial wash
// ─────────────────────────────────────────────
export const LightLeak = ({ position = 'top-right' }) => {
  const positions = {
    'top-right': { top: '-10%', right: '-10%' },
    'top-left': { top: '-10%', left: '-10%' },
    'bottom-right': { bottom: '-10%', right: '-10%' },
    'bottom-left': { bottom: '-10%', left: '-10%' },
    'center': { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' },
  };
  return (
    <div
      aria-hidden
      className="light-leak pointer-events-none absolute w-[60vw] h-[60vw] max-w-[800px] max-h-[800px] rounded-full opacity-[0.10]"
      style={{
        ...positions[position],
        background: 'radial-gradient(circle, rgba(184,136,74,0.50), rgba(194,97,58,0.10) 35%, transparent 70%)',
        filter: 'blur(50px)',
      }}
    />
  );
};

// ─────────────────────────────────────────────
// 6. Magnetic / animated headline (word-by-word reveal)
// ─────────────────────────────────────────────
export const KineticHeadline = ({ text, className = '', as: Tag = 'h2', delay = 0 }) => {
  const words = text.split(' ');
  return (
    <Tag className={`kinetic-headline ${className}`}>
      {words.map((w, i) => (
        <span key={i} className="kinetic-word" style={{ animationDelay: `${delay + i * 70}ms` }}>
          <span>{w}{i < words.length - 1 ? '\u00A0' : ''}</span>
        </span>
      ))}
    </Tag>
  );
};

// ─────────────────────────────────────────────
// 7. Ambient background — fixed radial vignette + soft moving gold orb
// ─────────────────────────────────────────────
export const AmbientBackground = () => (
  <>
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0"
      style={{
        background: 'radial-gradient(ellipse at top, rgba(184,136,74,0.04), transparent 55%), radial-gradient(ellipse at bottom, rgba(194,97,58,0.03), transparent 65%)',
      }}
    />
    <div aria-hidden className="ambient-orb pointer-events-none fixed -top-32 -right-32 w-[40vw] h-[40vw] max-w-[600px] max-h-[600px] z-0 rounded-full"
      style={{ background: 'radial-gradient(circle, rgba(184,136,74,0.07), transparent 70%)', filter: 'blur(60px)' }} />
  </>
);
